import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Building2, CreditCard } from 'lucide-react';
import { LOGO_SIZE } from '@vidmakr/shared';
import { ImagePicker } from '@/components/ImagePicker';
import { Badge, Button, Card, ConfirmModal, Field, Input, PageHeader, Progress } from '@/components/ui';
import { useLogos, useOrg } from '@/lib/hooks';
import { reducers } from '@/lib/stdb';
import { attempt, toast, errorMessage } from '@/lib/toast';
import { formatBytes } from '@/lib/format';
import { useOrgPlan } from '@/lib/billing';
import { StorageConnections } from '@/components/StorageSources';
import { Integrations } from '@/components/Integrations';
import { ColorPalettes } from '@/components/ColorPalettes';

export function SettingsPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { org, can, role } = useOrg(orgId);
  const logos = useLogos();
  const [name, setName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const plan = useOrgPlan(orgId);
  useEffect(() => setName(org?.name ?? ''), [org?.name]);
  if (!org) return null;


  return (
    <>
      <PageHeader title="Settings" subtitle={org.name} />
      <div className="grid max-w-3xl gap-6">
        <Card className="p-5">
          <h2 className="mb-4 font-semibold">General</h2>
          <div className="mb-5 flex items-center gap-4">
            <ImagePicker
              url={logos.get(org.id) ?? ''}
              endpoint={`/orgs/${org.id}/logo`}
              maxPx={LOGO_SIZE}
              size={64}
              label="logo"
              disabled={!can('admin')}
              fallback={<Building2 size={26} className="text-faint" />}
              onChange={(url) => attempt(() => reducers().setOrgLogo({ orgId: org.id, url }), url ? 'Logo updated' : 'Logo removed')}
            />
            <div className="text-sm">
              <div className="font-medium">Organization logo</div>
              <div className="text-xs text-faint">{can('admin') ? 'Shown in the sidebar. Click or drop a square image.' : 'Only admins can change it.'}</div>
            </div>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Field label="Organization name">
                <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!can('admin')} />
              </Field>
            </div>
            {can('admin') ? (
              <Button variant="primary" disabled={name === org.name} onClick={() => void attempt(() => reducers().renameOrg({ orgId: org.id, name }), 'Saved')}>
                Save
              </Button>
            ) : null}
          </div>
          <div className="mt-3 text-xs text-faint">Your role: {role}</div>
        </Card>

        <ColorPalettes orgId={org.id} canManage={can('admin')} />

        <Card className="p-5">
          <h2 className="mb-4 font-semibold">Storage</h2>
          <div className="mb-2 flex justify-between text-sm">
            <span className="text-muted">Used</span>
            <span>
              {formatBytes(org.storageUsedBytes)} of {formatBytes(org.storageLimitBytes)}
            </span>
          </div>
          <Progress value={org.storageUsedBytes / Math.max(1, org.storageLimitBytes)} />
          <p className="mt-3 text-xs text-faint">Originals, proxies and exports count toward the limit. Delete old exports to free space.</p>
        </Card>

        <StorageConnections orgId={org.id} canManage={can('admin')} />

        <Integrations orgId={org.id} canManage={can('admin')} />

        {plan?.enabled ? (
          <Card className="flex flex-wrap items-center justify-between gap-3 p-5">
            <div className="flex items-center gap-2">
              <CreditCard size={16} className="text-muted" />
              <h2 className="font-semibold">Plan & billing</h2>
              <Badge tone={plan.limits.plan.id === 'free' ? 'neutral' : 'accent'}>{plan.limits.plan.label}</Badge>
            </div>
            <Button onClick={() => navigate(`/orgs/${org.id}/billing`)}>Plans and usage</Button>
          </Card>
        ) : null}

        {role === 'owner' ? (
          <Card className="border-danger/40 p-5">
            <h2 className="mb-1 font-semibold text-danger">Delete organization</h2>
            <p className="mb-4 text-sm text-muted">Deletes all projects, assets and exports. For a few days the site administrators can still restore it; after that it is gone for good.</p>
            <Button variant="danger" onClick={() => setDeleting(true)}>
              Delete organization
            </Button>
          </Card>
        ) : null}
      </div>
      <ConfirmModal
        open={deleting}
        onClose={() => setDeleting(false)}
        title="Delete organization"
        danger
        confirmLabel="Delete organization"
        requireText={org.name}
        message="All projects, files and exports are removed. For a few days the site administrators can still restore them; after that they are gone for good."
        onConfirm={async () => {
          if (await attempt(() => reducers().deleteOrg({ orgId: org.id, confirmName: org.name }), 'Organization deleted')) navigate('/');
        }}
      />
    </>
  );
}
